/**
 * Pure-data English catalogue for namespace 'extra' (W1-08 formats-legacy-respack; imports only core/util/types):
 * import report entries of `decodeExtra` (extra.json: shader effects and background videos). The same keys are
 * listed in `ExtraDecodeResult.phiraFatal` for problems that stop Phira from loading the chart. Params are named
 * in braces; the import report shows an entry's `count` and `path` next to the text.
 */

import type { MessageCatalog } from '../../core/util/types';

const FATAL = 'Phira would not load the chart because of it.';

const catalog: MessageCatalog = {
  ns: 'extra',
  locale: 'en',
  messages: {
    // ---- File-level problems ----
    'report.invalidUtf8': `extra.json is not valid UTF-8 text. It was read anyway; ${FATAL}`,
    'report.bom': `extra.json starts with a byte order mark (BOM). It was ignored; ${FATAL}`,
    'report.syntax': `extra.json is not valid JSON (line {line}, column {col}). Its effects and videos were left out; ${FATAL}`,
    'report.notObject': `extra.json does not contain a JSON object. Its content was left out; ${FATAL}`,
    'report.missingBpm': `extra.json has no "bpm" field, which Phira requires. The chart's BPM was used; ${FATAL}`,

    // ---- Field problems Phira refuses (repaired) ----
    'report.duplicateKey': `The field "{field}" is written twice; the last value was used. ${FATAL}`,
    'report.wrongType': `The field "{field}" has the wrong kind of value and was left out. ${FATAL}`,
    'report.missingField': `The required field "{field}" is missing; the item was left out. ${FATAL}`,
    'report.floatInTriple': `A beat in "{field}" uses numbers with a decimal point. They were rounded; ${FATAL}`,
    'report.floatInInt': `"{field}" must be a whole number. It was rounded; ${FATAL}`,
    'report.tripleRange': `A beat in "{field}" has a part out of range. It was corrected; ${FATAL}`,
    'report.outOfRange': `The number in "{field}" is too large. The item was left out; ${FATAL}`,
    'report.colourNotBytes': `The colour "{field}" must be four whole numbers from 0 to 255. It was converted; ${FATAL}`,
    'report.varShape': `The value of "{field}" is neither a number, a pair of numbers, a colour nor a list of keyframes. It was left out; ${FATAL}`,
    'report.emptyKeyframes': `"{field}" has an empty keyframe list, which makes Phira crash. It was left out.`,
    'report.unknownShader': `"{name}" is not one of Phira's built-in shaders, and custom shader paths must start with "/". The effect was left out; ${FATAL}`,
    'report.shaderPath': `The shader path "{name}" names no file. The effect was left out; ${FATAL}`,
    'report.videoScale': `The video scale "{value}" is not one of cropCenter, inside or fit. cropCenter was used; ${FATAL}`,

    // ---- Changed / normalised ----
    'report.tripleZeroDen': 'A beat in "{field}" divides by zero, so Phira cannot place it. The item was left out.',
    'report.invalidBpm':
      "The BPM list of extra.json has a value that is not a positive number. The chart's BPM was used for its times.",
    'report.bpmConverted':
      'extra.json has its own BPM list. {count} times were converted through seconds to chart beats, so effects and videos stay at the same moments.',
    'report.bpmInexact': 'Some extra.json times could not be expressed exactly in chart beats and were rounded.',
    'report.timeOutOfRange':
      'Some extra.json times lie too far from the start of the chart once converted to chart beats. They were moved to the nearest beat the editor supports.',
    'report.duplicateVar': 'The effect variable "{field}" is written twice; the last value was used, as Phira does.',
    'report.keyframeKeyDropped': 'Keyframes in extra.json cannot have "{field}"; Phira ignores it, so it was left out.',
    'report.videoLeadIn':
      'The video {field} keyframes start at beat 0 of an extra.json BPM list that begins before beat 0. Phira continues the first keyframe back to the start of the chart there; the chart now shows the default {field} until the first keyframe.',

    // ---- Flagged (kept exactly) ----
    'report.keyframesUnsorted':
      'The keyframes of "{field}" are not in time order. They were kept and play the way Phira plays them forward; after seeking backwards, Phira can show other values until the chart is restarted.',
    'report.effectNeverActive': 'An effect ends before it starts, so it is never shown.',
  },
};

export default catalog;
